<script lang="ts" setup>
import type { Storage, Features } from '@d/sdk'
import UIcon from '@d/form/components/UIcon.vue'
import { ConnectionState } from '@d/sdk'
import { mdiAccount, mdiCalendarEdit, mdiCloud, mdiCloudAlert, mdiCloudDownload, mdiConnection, mdiMapMarker, mdiPen, mdiStarCircle, mdiTrashCan } from '@mdi/js'
import { getTimeString } from '../../../helper/time'
import { getItemStateColor, getItemStateText } from './state'

interface StorageCardProps {
  item: Storage
}

const props = defineProps<StorageCardProps>()

const emit = defineEmits(['action:edit', 'action:delete', 'action:recover', 'action:backup'])

const item = useVModel(props, 'item')

const { t, locale } = useI18n({
  useScope: 'local',
})

type FeatureKey = keyof Features

const activeFeaturesText = computed(() => {
  const result: string[] = []
  const features = item.value.features
  Object.keys(features).forEach((feature: string) => {
    const active = features[feature as FeatureKey]
    if (active) {
      result.push(t(`features.${feature}`))
    }
  })
  return result
})
</script>

<template>
  <v-card
    ripple
    class="pa-2 mt-2 storage-entry"
    rounded="xl"
  >
    <div>
      <div class="d-flex" style="align-items: center;">
        <v-sheet class="ml-2 mr-4 pa-2" color="primary" rounded="pill">
          <UIcon :icon="item.state === ConnectionState.CONNECTED ? mdiCloud : mdiCloudAlert" size="32" color="background2" />
        </v-sheet>
        <div style="display: flex; flex-grow:1; justify-content: space-between" class="py-2">
          <div style="flex-grow: 1;">
            <div>
              <div class="text-h6">
                {{ item.name }}
              </div>
              <div class="text-medium-emphasis">
                <div v-if="item.config.username">
                  <UIcon class="mr-2" :icon="mdiAccount" />
                  <span class="text-disabled">
                    {{ item.config.username }}
                  </span>
                </div>
                <UIcon class="mr-2" :icon="mdiMapMarker" />{{ item.config.url }}
                <div v-if="item.lastModified">
                  <v-tooltip :text="t('lastModified')" location="bottom">
                    <template #activator="{ props: activator }">
                      <span v-bind="activator">
                        <UIcon class="mr-2" :icon="mdiCalendarEdit" />
                        <span>{{ getTimeString(locale, item.lastModified) }}</span>
                      </span>
                    </template>
                  </v-tooltip>
                </div>
                <div>
                  <v-tooltip :text="t('activeFeatures')" location="bottom">
                    <template #activator="{ props: activator }">
                      <span v-bind="activator">
                        <UIcon class="mr-2" :icon="mdiStarCircle" :color="activeFeaturesText.length === 0 ? 'error' : undefined" />
                        <span>{{ activeFeaturesText.join(', ') || t('noActiveFeatures') }}</span>
                      </span>
                    </template>
                  </v-tooltip>
                </div>
              </div>
            </div>
          </div>
          <div v-if="item.state === ConnectionState.CONNECTED" style="display: flex; align-items: center">
            <div class="card-actions">
              <v-tooltip>
                <template #activator="{ props: activator }">
                  <v-btn variant="text" rounded="0" v-bind="activator" @click="emit('action:edit', item)">
                    <UIcon :icon="mdiPen" />
                  </v-btn>
                </template>
                {{ t('actions.edit') }}
              </v-tooltip>
              <v-tooltip>
                <template #activator="{ props: activator }">
                  <v-btn v-bind="activator" variant="text" rounded="0" @click="emit('action:backup', item)">
                    <UIcon :icon="mdiCloudDownload" />
                  </v-btn>
                </template>
                <span>{{ t('actions.backup') }}</span>
              </v-tooltip>
              <v-tooltip>
                <template #activator="{ props: activator }">
                  <v-btn color="error" rounded="0" variant="text" v-bind="activator" @click="emit('action:delete', item)">
                    <UIcon :icon="mdiTrashCan" />
                  </v-btn>
                </template>
                {{ t('actions.delete') }}
              </v-tooltip>
            </div>
          </div>
          <div v-else style="display: flex; justify-content: center; align-items: center">
            <div>
              <v-tooltip location="bottom">
                <template #activator="{ props: activator }">
                  <v-btn
                    v-bind="activator"
                    color="error"
                    variant="text"
                    rounded="0"
                    @click="emit('action:recover', item)"
                  >
                    <UIcon :icon="mdiConnection" :color="getItemStateColor(item.state)" :size="32" />
                  </v-btn>
                </template>
                {{ t(`connectionState.${getItemStateText(item.state)}`) }}
              </v-tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<style lang="css" scoped>
.storage-entry {
  background-color: rgba(var(--v-theme-on-surface), 0.05);
}

.card-actions {
  display: flex;
  flex-direction: column;
}
</style>

<i18n lang="yaml">
    de:
      actions:
        edit: Bearbeiten
        delete: Löschen
        errorHandling: Fehlerbehandlung
        backup: Backup
      lastModified: Zuletzt geändert
      connectionState:
        success: Verbunden
        auth: Autorisierung fehlgeschlagen
        missingPermissions: Fehlende Berechtigungen auf Server
        server: Verbindung zu Dienst fehlgeschlagen
      activeFeatures: Aktive Funktionen
      noActiveFeatures: Keine Funktionen aktiviert
      features:
        read: Lesen
        write: Schreiben
        versioning: Versionierung
        trashbin: Papierkorb
    en:
      actions:
        edit: Edit
        delete: Delete
        errorHandling: Error handling
        backup: Backup
      lastModified: Last Modified
      connectionState:
        success: Connected
        auth: Authorization failed
        missingPermissions: Fehlende Berechtigungen auf Server
        server: Connection to service failed
      activeFeatures: Active features
      noActiveFeatures: No features active
      features:
        read: Read
        write: Write
        versioning: Versioning
        trashbin: Trashbin
    </i18n>
